import React, { useMemo } from 'react';
import moment from 'moment';
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { localizedLabel, maskToIndexes } from '../../../../../utils/maskUtils';
import { countFree, resourceAvailabilityForSpan } from '../../../../utils/resourceAvailability';
import { roomColorForIndex } from '../../../../roomColors';
import { useOrders } from '../../../../swr/useOrders';
import { useUnavailabilities } from '../../../../swr/useUnavailabilities';
import { DATE_FORMAT } from '../../../../calendarWrapper/CalendarWrapper';
import { EditOrderFormValues } from '../../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';

const AREA_SELECT_ID = 'create-order-area-select';

export const AreaSelect = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();

  const orderPageId: string = editOrderState.orderPage.id;
  const schedulerConfigs = account.shared_resources?.scheduler_configs ?? [];
  const pageConfig = schedulerConfigs.find((config) =>
    config.allowed_masks?.some((allowedMask) => allowedMask.order_page_id === orderPageId),
  );
  const resourceOptions =
    pageConfig?.allowed_masks?.find((allowedMask) => allowedMask.order_page_id === orderPageId)?.resource_options ?? [];

  const { date, timeSeconds, privateOrderEndTimeSeconds } = values;
  const hasSlot = Boolean(date) && privateOrderEndTimeSeconds > timeSeconds;
  // The orders of today are fetched while no date is chosen, so no request goes out with an empty date.
  const fetchDate = date || moment().format(DATE_FORMAT);
  const { items: ordersByDate } = useOrders({ startDate: fetchDate, endDate: fetchDate });
  const { items: unavailabilitiesByDate } = useUnavailabilities({ startDate: fetchDate, endDate: fetchDate });

  const availability = useMemo(
    () =>
      hasSlot && pageConfig
        ? resourceAvailabilityForSpan({
            config: pageConfig,
            orders: ordersByDate[date] ?? [],
            unavailabilities: unavailabilitiesByDate[date] ?? [],
            startSeconds: timeSeconds,
            endSeconds: privateOrderEndTimeSeconds,
          })
        : undefined,
    [hasSlot, pageConfig, ordersByDate, unavailabilitiesByDate, date, timeSeconds, privateOrderEndTimeSeconds],
  );

  if (resourceOptions.length === 0) {
    return null;
  }

  const activity = account.activity_label_singular?.[language] ?? t('passes.activity_fallback_singular');

  return (
    <Grid container flexDirection="column" gap={0.5}>
      <FormControl fullWidth>
        <InputLabel id={`${AREA_SELECT_ID}-label`} shrink>
          {t('calendar.create_order.areaOptional')}
        </InputLabel>
        <Select
          labelId={`${AREA_SELECT_ID}-label`}
          id={AREA_SELECT_ID}
          label={t('calendar.create_order.areaOptional')}
          notched
          displayEmpty
          value={values.resourceOptionId}
          onChange={(event) => {
            void setFieldValue('resourceOptionId', event.target.value);
          }}>
          <MenuItem value="">{t('calendar.create_order.areaNoPreference')}</MenuItem>
          {resourceOptions.map((option, index) => (
            <MenuItem key={option.id} value={option.id}>
              <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={1}>
                <Grid container item alignItems="center" gap={1} flexWrap="nowrap" xs>
                  <Grid
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: roomColorForIndex(index).dot,
                    }}
                  />
                  <span>{localizedLabel(option.label, language, option.id)}</span>
                </Grid>
                {availability && (
                  <Typography variant="caption" color="text.secondary">
                    {t('calendar.create_order.areaFree', {
                      count: countFree(availability, maskToIndexes(option.mask)),
                    })}
                  </Typography>
                )}
              </Grid>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary">
        {t('calendar.create_order.areaAutoHint', { activity })}
      </Typography>
    </Grid>
  );
};
